import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
    Box, Button, Checkbox, Flex, Icon, Input, Spinner, Stack, Text, Wrap, WrapItem, useColorModeValue,
} from "@chakra-ui/react";
import { MdSave, MdSettings } from "react-icons/md";
import Card from "horizon/components/card/Card";
import usePaymentsConfig from "./usePaymentsConfig";
import { PAYMENT_METHOD_OPTIONS } from "./paymentsConstants";
import PaymentField from "./PaymentField";
import PaymentPanelHeader from "./PaymentPanelHeader";

const LOCKED_CURRENCY = "eur";

/**
 * Builds the form initial state from a normalized config object.
 * @param {Object|null} config - Normalized config from usePaymentsConfig
 * @returns {Object}
 */
function buildInitialForm(config) {
    if (!config) {
        return {
            card_threshold: "", hold_expiration_hours: 72,
            allowed_methods_low: ["card", "sepa_credit_transfer"], allowed_methods_high: ["sepa_credit_transfer"],
        };
    }
    return {
        card_threshold: String(config.card_threshold ?? ""),
        hold_expiration_hours: config.hold_expiration_hours ?? 72,
        allowed_methods_low: [...config.allowed_methods_low],
        allowed_methods_high: [...config.allowed_methods_high],
    };
}

/**
 * Toggles a method value inside an array (immutably).
 * @param {Array} list - Current list
 * @param {string} value - Method key to toggle
 * @returns {Array}
 */
function toggleMethod(list, value) {
    return list.includes(value) ? list.filter((m) => m !== value) : [...list, value];
}

/**
 * Validates form payload before submission.
 * @param {Object} form - Form state
 * @returns {string|null} Error message or null
 */
function validateForm(form) {
    const threshold = Number(form.card_threshold);
    if (Number.isNaN(threshold) || threshold < 0) return "Le seuil doit être un nombre positif";
    const hours = Number(form.hold_expiration_hours);
    if (Number.isNaN(hours) || hours < 1) return "La durée du hold doit être supérieure à 0";
    if (form.allowed_methods_low.length === 0) return "Au moins une méthode sous le seuil est requise";
    if (form.allowed_methods_high.length === 0) return "Au moins une méthode au-dessus du seuil est requise";
    return null;
}

/**
 * Builds the payload sent to PUT /config from form state.
 * @param {Object} form - Form state
 * @returns {Object}
 */
function buildPayload(form) {
    return {
        card_threshold: Number(form.card_threshold), currency: LOCKED_CURRENCY,
        hold_expiration_hours: Number(form.hold_expiration_hours),
        allowed_methods_low: form.allowed_methods_low, allowed_methods_high: form.allowed_methods_high,
    };
}

/**
 * Submits the form payload via the save callback and surfaces toasts.
 * @param {Object} args - Submission arguments
 */
async function submitForm({ form, save, setSaving }) {
    const err = validateForm(form);
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
        await save(buildPayload(form));
        toast.success("Configuration mise à jour");
    } catch (error) {
        const msg = error?.response?.data || "Échec de la mise à jour";
        toast.error(typeof msg === "string" ? msg : "Échec de la mise à jour");
    } finally {
        setSaving(false);
    }
}

/**
 * Renders the threshold + currency + hold hours input row.
 * @param {Object} props - { form, onChange }
 * @returns {React.ReactElement}
 */
function NumberFieldsRow({ form, onChange }) {
    return (
        <Stack direction={{ base: "column", md: "row" }} spacing="16px">
            <PaymentField label="Seuil carte/SEPA" type="number" value={form.card_threshold}
                onChange={(v) => onChange("card_threshold", v)}
                helper="Au-dessus de ce montant, seul le SEPA est proposé" />
            <PaymentField label="Devise" helper="SEPA = zone euro (verrouillé)">
                <Input value="EUR" isReadOnly borderRadius="12px" />
            </PaymentField>
            <PaymentField label="Durée du hold (heures)" type="number" value={form.hold_expiration_hours}
                onChange={(v) => onChange("hold_expiration_hours", v)}
                helper="Délai avant annulation auto d'un SEPA non confirmé" />
        </Stack>
    );
}

/**
 * Renders a checkbox group for allowed payment methods.
 * @param {Object} props - { title, hint, selected, onToggle }
 * @returns {React.ReactElement}
 */
function MethodsGroup({ title, hint, selected, onToggle }) {
    const titleColor = useColorModeValue("secondaryGray.900", "white");
    const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Box>
            <Text fontSize="sm" fontWeight="600" color={titleColor} mb="2px">{title}</Text>
            <Text fontSize="xs" color={subColor} mb="8px">{hint}</Text>
            <Wrap spacing="16px">
                {PAYMENT_METHOD_OPTIONS.map((opt) => (
                    <WrapItem key={opt.value}>
                        <Checkbox colorScheme="brand" isChecked={selected.includes(opt.value)}
                            onChange={() => onToggle(opt.value)}>
                            <Text fontSize="sm">{opt.label}</Text>
                        </Checkbox>
                    </WrapItem>
                ))}
            </Wrap>
        </Box>
    );
}

/**
 * Renders the panel body: loading spinner, error, or the configuration form.
 * @param {Object} props - Body props
 * @returns {React.ReactElement|null}
 */
function ConfigBody({ config, loading, error, form, onChange, onToggleLow, onToggleHigh, saving, onSave }) {
    if (loading && !config) return <Flex justify="center" py="32px"><Spinner color="brand.500" /></Flex>;
    if (error && !config) return <Text color="red.500" fontSize="sm">{typeof error === "string" ? error : "Erreur de chargement"}</Text>;
    if (!config) return null;
    return (
        <Stack spacing="24px">
            <NumberFieldsRow form={form} onChange={onChange} />
            <MethodsGroup title="Sous le seuil" selected={form.allowed_methods_low} onToggle={onToggleLow}
                hint="Méthodes proposées au client lorsque le montant est inférieur au seuil" />
            <MethodsGroup title="Au-dessus ou égal au seuil" selected={form.allowed_methods_high} onToggle={onToggleHigh}
                hint="Méthodes proposées au client lorsque le montant atteint ou dépasse le seuil" />
            <Box>
                <Button onClick={onSave} isLoading={saving} loadingText="Enregistrement..."
                    leftIcon={<Icon as={MdSave} />} colorScheme="brand">Enregistrer la configuration</Button>
            </Box>
        </Stack>
    );
}

/**
 * Configuration panel for Stripe payment methods (threshold + currency + hold + methods).
 * @returns {React.ReactElement}
 */
function PaymentsConfigPanel() {
    const { config, loading, error, save, refresh } = usePaymentsConfig();
    const [form, setForm] = useState(buildInitialForm(null));
    const [saving, setSaving] = useState(false);
    useEffect(() => { if (config) setForm(buildInitialForm(config)); }, [config]);
    const onChange = (key, value) => setForm((f) => ({ ...f, [key]: value }));
    const onToggleLow = (v) => setForm((f) => ({ ...f, allowed_methods_low: toggleMethod(f.allowed_methods_low, v) }));
    const onToggleHigh = (v) => setForm((f) => ({ ...f, allowed_methods_high: toggleMethod(f.allowed_methods_high, v) }));
    const onSave = () => submitForm({ form, save, setSaving });
    return (
        <Card>
            <PaymentPanelHeader icon={MdSettings} title="Configuration des paiements"
                subtitle="Effet immédiat — pas de redéploiement requis" onRefresh={refresh} refreshing={loading} />
            <ConfigBody config={config} loading={loading} error={error} form={form} onChange={onChange}
                onToggleLow={onToggleLow} onToggleHigh={onToggleHigh} saving={saving} onSave={onSave} />
        </Card>
    );
}

export default PaymentsConfigPanel;
